import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useSeoConfig } from "../lib/seo";

const Stars = ({ value, size = 15 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} von 5 Sternen`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        strokeWidth={1.5}
        className={i <= Math.round(value) ? "text-[#FF7A00]" : "text-[#CBD5E1]"}
        fill={i <= Math.round(value) ? "#FF7A00" : "none"}
      />
    ))}
  </span>
);

export const Reviews = () => {
  const [data, setData] = useState(null);
  const cfg = useSeoConfig();
  const profileUrl = cfg?.site?.gbp_url;

  useEffect(() => {
    api.get("/reviews").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data || !data.count) return null;

  return (
    <section className="bg-white border-y border-[#E2E8F0]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[#B24E00] font-semibold">Bewertungen</div>
          <h2 className="nx-head mt-5 text-3xl md:text-4xl font-semibold text-[#0F172A]">
            Das sagen unsere Kunden
          </h2>
          <p className="mt-5 text-base text-[#475569] leading-relaxed">
            Ihre Zufriedenheit ist unser Anspruch. Entdecken Sie echte Erfahrungen unserer Kunden mit NENOX.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4" data-testid="reviews-summary">
          <Stars value={data.average} size={22} />
          <div className="text-lg text-[#0F172A]">
            <strong className="nx-head text-2xl">{data.average.toLocaleString("de-DE", { minimumFractionDigits: 1 })}</strong>
            <span className="text-[#64748B] text-sm"> von 5 · {data.count} Google {data.count === 1 ? "Bewertung" : "Bewertungen"}</span>
          </div>
          {profileUrl && (
            <a
              href={profileUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="reviews-google-link"
              className="nx-btn-ghost !px-6 !py-3"
            >
              Alle Google-Bewertungen ansehen <ExternalLink size={14} strokeWidth={1.8} />
            </a>
          )}
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {data.items.map((r) => (
            <figure key={r.review_id} className="nx-card p-7 flex flex-col" data-testid="review-card">
              <Stars value={r.rating} />
              <blockquote className="mt-5 text-sm md:text-base text-[#334155] leading-relaxed flex-1">
                „{r.text}“
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-[#E2E8F0] text-xs text-[#64748B]">
                <span className="text-[#0F172A] font-semibold">{r.author}</span>
                {r.place ? ` · ${r.place}` : ""} · Google Bewertung
                {r.date ? ` · ${r.date}` : ""}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};
